import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import {
  useDefaultEditorStyle,
  getComponentById,
  getDataSourceByKeys,
  DATA_SOURCE_KEY_MAPPING,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  useSiteConfig,
  useIsBusinessHourSectionEnabled,
  useEditorStatusContext,
  editorStatusLoadingWrapperForPromise,
  useUserAccountCustomer,
} from '@bingads-webui-campaign-react/smart-page-context';
import { SmartBusinessHoursEditorWrapper } from '@bingads-webui-campaign-react/smart-component-business-hours';

import { ContactMethods } from './components/contact-methods';
import { dataProperties } from './data-config';
import { getErrors } from './errors';
import { proxyReadContactComponentConfig } from './components/component-config-reader';
import { getCustomerPayload, getAccountPayload } from './utils/get-account-customer-payload';

const componentName = 'ContactEditor';

export const SmartContactEditor = ({
  i18n,
  componentId,
  activeDataProperty,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    editorContentClassName,
  } = useDefaultEditorStyle();

  const {
    updateAccountWithLog,
    updateCustomerWithLog,
    readAccountWithLog,
    readCustomerWithLog,
  } = useUserAccountCustomer({ postponeRead: true, componentName });

  const [editorStatus, dispatchEditorStatus] = useEditorStatusContext();
  const isBusinessHourSectionEnabled = useIsBusinessHourSectionEnabled();
  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const componentConfig = getComponentById(siteConfig, componentId);
  const {
    showEmail,
    showPhone,
    showLocation,
    showHours,
    showMap,
  } = proxyReadContactComponentConfig(componentConfig);
  const [
    [email],
    phoneNumber,
    locationInfo,
    businessHours,
  ] = getDataSourceByKeys(siteConfig, [
    DATA_SOURCE_KEY_MAPPING.Emails,
    DATA_SOURCE_KEY_MAPPING.PhoneNumber,
    DATA_SOURCE_KEY_MAPPING.LocationInfo,
    DATA_SOURCE_KEY_MAPPING.BusinessHours,
  ], [[]]);

  const onToggle = (property, evt, checked) => {
    const update = {};

    if (property === dataProperties.phoneNumber) {
      update.showPhone = checked;
    } else if (property === dataProperties.emails) {
      update.showEmail = checked;
    } else if (property === dataProperties.locationInfo) {
      update.showLocation = checked;
      // "undefined" is a status to indicate a user has never used the map.
      // So, only change the showMap when user has used the map
      if (!checked && !_.isUndefined(showMap)) {
        update.showMap = false;
      }
    } else if (property === dataProperties.businessHours) {
      update.showHours = checked;
    } else if (property === dataProperties.bingMap) {
      update.showMap = checked;
    }

    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: {
        ...componentConfig,
        ...update,
      },
    });
  };

  const { errors } = useMemo(() => getErrors({
    showPhone,
    phoneNumber,
    showEmail,
    email,
    showLocation,
    locationInfo,
    showHours,
    businessHours,
    i18n,
  }), [
    businessHours, email, i18n, locationInfo,
    phoneNumber, showEmail, showHours, showLocation, showPhone,
  ]);

  const hoursEnableChange = (hoursEnabled) => {
    onToggle(dataProperties.businessHours, {}, hoursEnabled);
  };

  const syncContactInfoToUAC = useCallback(() => {
    let syncPhonePromise = Promise.resolve(null);
    let syncLocationPromise = Promise.resolve(null);

    if (showPhone && !errors[dataProperties.phoneNumber]) {
      syncPhonePromise = readCustomerWithLog({})
        .then((customer) => {
          const { hasDelta, updatedCustomer } = getCustomerPayload(customer, phoneNumber);
          return hasDelta ? updateCustomerWithLog({ Customer: updatedCustomer }) : null;
        });
    }

    if (showLocation && !errors[dataProperties.locationInfo]) {
      const rawAddress = editorStatus.buffer && (editorStatus.buffer.rawAddress || {});
      const { countryRegionIso2 } = rawAddress;
      // We only support US address update to account for UP
      if (countryRegionIso2 === 'US') {
        syncLocationPromise = readAccountWithLog({})
          .then((account) => {
            const {
              hasDelta,
              updatedAccount,
            } = getAccountPayload(account, locationInfo, rawAddress);
            return hasDelta ? updateAccountWithLog({ Account: updatedAccount }) : null;
          });
      }
    }

    return Promise.all([syncPhonePromise, syncLocationPromise])
      .catch(() => null)
      .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus));
  }, [
    showPhone, errors, showLocation, dispatchEditorStatus, readCustomerWithLog,
    phoneNumber, updateCustomerWithLog, readAccountWithLog, editorStatus.buffer,
    locationInfo, updateAccountWithLog,
  ]);

  useEffect(() => {
    dispatchEditorStatus({
      type: 'SET_ON_EXIT_COMPONENT_EDITING_EFFECT',
      payload: syncContactInfoToUAC,
    });
  }, [syncContactInfoToUAC, dispatchEditorStatus]);

  return (
    <div>
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Business contact information'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Include at least one of the following for customers to contact or visit your business.'))}
      </div>
      <div className={classNames(editorContentClassName, 'smart-contact-editor')}>
        <ContactMethods
          i18n={i18n}
          componentId={componentId}
          activeDataProperty={activeDataProperty}
          onToggle={onToggle}
          errors={errors}
          locationInfo={locationInfo}
        />
        {
          !isBusinessHourSectionEnabled && <SmartBusinessHoursEditorWrapper
            i18n={i18n}
            componentId={componentId}
            isInContact
            showHeaderToggle
            headerToggleChecked={showHours}
            onHeaderToggleChange={hoursEnableChange}
          />
        }
      </div>
    </div>
  );
};

SmartContactEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  activeDataProperty: PropTypes.string,
};

SmartContactEditor.defaultProps = {
  activeDataProperty: null,
};
